import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, MapPin, ChevronsUpDown, ChevronUp, ChevronDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { colorOf, fmtDate, persenLabel } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { EntityFormDialog } from "@/components/EntityFormDialog";

const Cell = ({ col, row, testId, identitas }) =>
  col.type === "status" ? <StatusBadge status={row[col.key]} testId={testId} /> :
  // Kolom `percent` numerik: nilai aslinya ditampilkan apa adanya, label 3-status-nya diwarnai.
  col.type === "percent" ? <StatusBadge status={row[col.key] == null ? null : `${row[col.key]}%`} color={colorOf(persenLabel(row[col.key]))} testId={testId} /> :
  col.type === "date" ? <span className="font-mono text-xs text-slate-300" data-testid={testId}>{fmtDate(row[col.key])}</span> :
  // `luas_ha` turunan dari geometri dan bisa NULL (baris tanpa geom), jadi bukan identitas baris —
  // makanya netral, bukan sky seperti kolom default.
  col.type === "luas" ? <span className="font-mono text-sm text-slate-300" data-testid={testId}>{row[col.key] == null ? "—" : `${Math.round(row[col.key])} ha`}</span> :
  // `?? "—"`: React merender null sebagai sel hampa, dan sel hampa sulit dibedakan dari data yang
  // gagal dimuat. Kolom non-identitas boleh kosong (mis. flight_id di baris lama).
  // Angka/teks identitas dicetak tebal — itu yang dipakai testid, tombol aksi, dan URL PUT.
  col.key === identitas
    ? <span className="font-mono text-sm font-semibold text-sky-300" data-testid={testId}>{row[col.key] ?? "—"}</span>
    : <span className="font-mono text-sm text-slate-300" data-testid={testId}>{row[col.key] ?? "—"}</span>;

// Nilai kosong memang data sah di beberapa kolom: `luas_ha` NULL untuk baris tanpa geometri,
// `tanggal_ukur` NULL untuk CP yang belum diukur, `block_id` NULL untuk flight tanpa blok.
// (`koordinat` TIDAK termasuk sejak 2026-10-22 — kolomnya wajib, jadi kosong hanya muncul di
// baris lama sebelum aturan itu.) Semuanya ditaruh di BAWAH pada kedua arah urutan — kalau ikut
// dibalik, baris kosong melompat ke atas begitu user menekan panah kedua, padahal justru baris
// itulah yang dicari.
const kosong = (v) => v === null || v === undefined || v === "";

// Satu pembanding per `type` kolom, supaya tidak ada per-kolom config: `type` sudah dipakai
// `Cell` untuk memilih renderer, jadi tabel dan sortir membaca deklarasi yang sama.
// Kolom status diurut menurut urutan `options` (Belum -> Dalam Proses -> Selesai), bukan
// alfabetis: alfabetis akan menaruh "Selesai" di tengah dan "Disetujui" di atas "Belum".
const compare = (col, a, b, dir) => {
  const va = a[col.key], vb = b[col.key];
  // Kosong selalu kalah (di bawah), dan itu TIDAK ikut dibalik `dir` — makanya checked sebelum dir.
  if (kosong(va) || kosong(vb)) return kosong(va) ? (kosong(vb) ? 0 : 1) : -1;
  const hasil =
    col.type === "status" ? col.options.indexOf(va) - col.options.indexOf(vb) :
    // `percent` dan `luas` sudah `float8` dari API, jadi ini perbandingan angka — bukan string,
    // yang akan mengurutkan "100" sebelum "62".
    col.type === "percent" || col.type === "luas" ? Number(va) - Number(vb) :
    // Tanggal ISO (YYYY-MM-DD) sudah urut secara leksikografis, jadi tidak perlu Date.
    String(va).localeCompare(String(vb));
  return dir === "asc" ? hasil : -hasil;
};

export default function EntityPage({ title, eyebrow, endpoint, prefix, columns, filterKey, idKey, layerKey, canCreate = true, canDelete = true }) {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("semua");
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  // Urutan awal = urutan server (null). Klik header -> asc, klik lagi -> desc, klik lagi -> asc;
  // tidak ada "kembali ke urutan server", reload sudah cukup dan tiga kondisi membingungkan.
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggleSort = (key) =>
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  // Identitas baris (testid, URL edit/hapus, field terkunci) TIDAK selalu kolom pertama:
  // Flight menampilkan Block ID di depan padahal yang unik dan jadi kunci adalah Flight ID.
  const identitas = idKey ?? columns[0].key;
  const filterCol = columns.find((c) => c.key === filterKey);

  const load = () => api.get(endpoint).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  // `percent` dan `date` tidak dicocokkan lewat kesamaan nilai, karena `options`-nya bukan nilai
  // yang tersimpan: `percent` memakai label rentang (0 / 1-99 / 100) supaya filter "Selesai"
  // menangkap 100, `date` memakai ada-tidaknya tanggal ("Sudah"/"Belum"). Sisanya kesamaan biasa.
  const cocok = (r) => (filterCol.type === "date" ? ((r[filterKey] ? "Sudah" : "Belum") === filter) : filterCol.type === "percent" ? persenLabel(r[filterKey]) === filter : r[filterKey] === filter);
  // `?? ""`: kolom ID bisnis boleh NULL di level kolom (unique index mengizinkan banyak NULL),
  // dan tanpa penjaga ini `.toLowerCase()` meledak dan halaman jadi kosong total.
  // Indeks asli dipakai sebagai tie-break supaya baris ber-nilai sama tidak bertukar posisi antar
  // render (testid baris ikut berpindah kalau itu terjadi) — sortir jadi stabil tanpa bergantung
  // pada jaminan stabilitas mesin JS.
  const visible = useMemo(() => {
    const hasil = rows.filter((r) =>
      (r[identitas] ?? "").toLowerCase().includes(search.toLowerCase()) && (filter === "semua" || cocok(r)));
    if (!sort.key) return hasil;
    const col = columns.find((c) => c.key === sort.key);
    return hasil
      .map((r, i) => [r, i])
      .sort(([a, i], [b, j]) => compare(col, a, b, sort.dir) || i - j)
      .map(([r]) => r);
  }, [rows, search, filter, sort, identitas, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (form) => {
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, form);
      else await api.post(endpoint, form);
      toast.success(editing ? "Data berhasil diperbarui" : "Data berhasil ditambahkan");
      setFormOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    try { await api.delete(`${endpoint}/${toDelete.id}`); toast.success(`${toDelete[identitas]} dihapus`); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 grid-bg min-h-screen">
      <div className="rise flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sky-400">{eyebrow}</p>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold mt-1" data-testid={`${prefix}-page-title`}>{title}</h2>
          <p className="text-sm text-slate-400 mt-1"><span data-testid={`${prefix}-count`}>{visible.length}</span> dari {rows.length} data ditampilkan</p>
        </div>
        {isAdmin ? (
          canCreate && <Button data-testid={`${prefix}-add-button`} onClick={() => { setEditing(null); setFormOpen(true); }} className="gap-2 rounded-full px-5"><Plus className="h-4 w-4" />Tambah Data</Button>
        ) : (
          <span data-testid="viewer-readonly-note" className="rounded-full border border-border px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-400">Mode Viewer · Hanya Lihat</span>
        )}
      </div>

      <div className="rise mt-8 flex flex-col gap-3 sm:flex-row" style={{ animationDelay: "80ms" }}>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input data-testid={`${prefix}-search-input`} placeholder={`Cari ${columns[0].label}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[#131B29]" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-56 bg-[#131B29]" data-testid={`${prefix}-status-filter`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="semua">Semua {filterCol.label}</SelectItem>
            {filterCol.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rise mt-5 overflow-hidden rounded-md border border-border bg-[#131B29]/90" style={{ animationDelay: "160ms" }}>
        <div className="overflow-x-auto">
          <Table data-testid={`${prefix}-table`}>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {columns.map((c) => (
                  <TableHead key={c.key} aria-sort={sort.key !== c.key ? "none" : sort.dir === "asc" ? "ascending" : "descending"}
                    className="font-mono text-[10px] uppercase tracking-widest text-slate-400 whitespace-nowrap">
                    {/* aria-sort di th yang membuat sortir terbaca pembaca layar; panahnya cuma
                        isyarat visual, jadi tidak boleh jadi satu-satunya penanda. */}
                    <button type="button" data-testid={`${prefix}-sort-${c.key}`} onClick={() => toggleSort(c.key)}
                      className={`inline-flex items-center gap-1 uppercase tracking-widest transition-colors hover:text-sky-300 ${sort.key === c.key ? "text-sky-400" : ""}`}>
                      {c.label}
                      {sort.key !== c.key ? <ChevronsUpDown className="h-3 w-3 opacity-50" />
                        : sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  </TableHead>
                ))}
                <TableHead className="font-mono text-[10px] uppercase tracking-widest text-slate-400 text-center">Peta</TableHead>
                {isAdmin && <TableHead className="text-right font-mono text-[10px] uppercase tracking-widest text-slate-400">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={columns.length + 2} className="py-10 text-center text-slate-500">Memuat data...</TableCell></TableRow>}
              {!loading && !visible.length && <TableRow><TableCell colSpan={columns.length + 2} className="py-10 text-center text-slate-500" data-testid={`${prefix}-empty`}>Tidak ada data</TableCell></TableRow>}
              {visible.map((r) => (
                <TableRow key={r.id} className="border-border/60 transition-colors hover:bg-sky-500/5" data-testid={`${prefix}-row-${r[identitas]}`}>
                  {columns.map((c) => <TableCell key={c.key}><Cell col={c} row={r} identitas={identitas} testId={`${prefix}-${r[identitas]}-${c.key}`} /></TableCell>)}
                  <TableCell className="text-center">
                    {r.has_geom ? (
                      <button type="button" aria-label={`Lihat ${r[identitas]} di peta`}
                        data-testid={`${prefix}-map-${r[identitas]}`}
                        onClick={() => navigate("/gis", { state: { fokus: { layer: layerKey, id: r[identitas] } } })}
                        className="rounded-sm p-1 text-emerald-400 transition-colors hover:bg-emerald-400/15 hover:ring-1 hover:ring-emerald-400/60">
                        <MapPin className="h-4 w-4" />
                      </button>
                    ) : <span className="text-slate-600">—</span>}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" data-testid={`${prefix}-edit-${r[identitas]}`} onClick={() => { setEditing(r); setFormOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                      {canDelete && <Button size="icon" variant="ghost" className="hover:text-rose-400" data-testid={`${prefix}-delete-${r[identitas]}`} onClick={() => setToDelete(r)}><Trash2 className="h-4 w-4" /></Button>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <EntityFormDialog open={formOpen} onOpenChange={setFormOpen} columns={columns} initial={editing} onSubmit={save} prefix={prefix} idKey={identitas} title={editing ? `Edit ${editing[identitas]}` : `Tambah ${title}`} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#131B29]" data-testid={`${prefix}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {toDelete?.[identitas]}?</AlertDialogTitle>
            <AlertDialogDescription>Data beserta geometrinya akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${prefix}-delete-cancel`}>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid={`${prefix}-delete-confirm`} onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
